import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home,
  Settings,
  Plus,
  Search,
  Database,
  Table2,
  FileText,
  Zap,
  User,
  LogOut,
  Moon,
  Sun,
  Sparkles,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

interface SearchResult {
  id: string;
  type: "base" | "table" | "form" | "automation";
  name: string;
  parentName?: string;
  icon?: string;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Toggle command palette with Cmd+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Search when query changes
  useEffect(() => {
    if (!search || !user) {
      setResults([]);
      return;
    }

    const searchItems = async () => {
      setLoading(true);
      try {
        const searchQuery = `%${search}%`;

        // Search bases
        const { data: bases } = await supabase
          .from("bases")
          .select("id, name, icon")
          .ilike("name", searchQuery)
          .limit(5);

        // Search tables
        const { data: tables } = await supabase
          .from("tables")
          .select("id, name, bases(name)")
          .ilike("name", searchQuery)
          .limit(5);

        // Search forms
        const { data: forms } = await supabase
          .from("forms")
          .select("id, name, tables(name)")
          .ilike("name", searchQuery)
          .limit(3);

        const searchResults: SearchResult[] = [
          ...(bases || []).map((b) => ({
            id: b.id,
            type: "base" as const,
            name: b.name,
            icon: b.icon,
          })),
          ...(tables || []).map((t: any) => ({
            id: t.id,
            type: "table" as const,
            name: t.name,
            parentName: t.bases?.name,
          })),
          ...(forms || []).map((f: any) => ({
            id: f.id,
            type: "form" as const,
            name: f.name,
            parentName: f.tables?.name,
          })),
        ];

        setResults(searchResults);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    };

    const timeout = setTimeout(searchItems, 200);
    return () => clearTimeout(timeout);
  }, [search, user]);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search or type a command..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Search Results */}
        {results.length > 0 && (
          <CommandGroup heading="Search Results">
            {results.map((result) => (
              <CommandItem
                key={`${result.type}-${result.id}`}
                onSelect={() => {
                  runCommand(() => {
                    if (result.type === "base") {
                      navigate(`/base/${result.id}`);
                    } else if (result.type === "form") {
                      navigate(`/form/${result.id}`);
                    }
                  });
                }}
              >
                {result.type === "base" && (
                  <span className="mr-2">{result.icon || "📊"}</span>
                )}
                {result.type === "table" && <Table2 className="mr-2 h-4 w-4" />}
                {result.type === "form" && <FileText className="mr-2 h-4 w-4" />}
                <span>{result.name}</span>
                {result.parentName && (
                  <span className="text-muted-foreground ml-2 text-xs">
                    in {result.parentName}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Quick Actions */}
        <CommandGroup heading="Quick Actions">
          <CommandItem onSelect={() => runCommand(() => navigate("/dashboard"))}>
            <Home className="mr-2 h-4 w-4" />
            <span>Go to Dashboard</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate("/settings"))}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Open Settings</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate("/templates"))}>
            <Sparkles className="mr-2 h-4 w-4" />
            <span>Browse Templates</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Create */}
        <CommandGroup heading="Create">
          <CommandItem
            onSelect={() =>
              runCommand(() => {
                // Dispatch custom event to open create dialog
                window.dispatchEvent(new CustomEvent("open-create-base"));
              })
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            <span>New Base</span>
            <span className="ml-auto text-xs text-muted-foreground">⌘N</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Preferences */}
        <CommandGroup heading="Preferences">
          <CommandItem onSelect={() => runCommand(toggleTheme)}>
            {theme === "dark" ? (
              <Sun className="mr-2 h-4 w-4" />
            ) : (
              <Moon className="mr-2 h-4 w-4" />
            )}
            <span>Toggle {theme === "dark" ? "Light" : "Dark"} Mode</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* Account */}
        <CommandGroup heading="Account">
          <CommandItem onSelect={() => runCommand(() => navigate("/settings"))}>
            <User className="mr-2 h-4 w-4" />
            <span>Profile Settings</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(signOut)}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign Out</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
